/**
 * What the host around the renderer can do. The desktop (Electron on the operator's own Windows
 * computer) can do all of it and declares nothing; a host that cannot installs
 * `window.capturePointHost` before the App renders and names what it hides. Paige's browser host
 * does so in `paige/paige-host.ts`; the table of what it hides and why is in
 * docs/engineering/workbench-port.md, "What Paige hides from the desktop workbench and why".
 */
export type HostFeature =
  /** Settings > Performance, the speed-boost status, the processing mode: measures the computer the app runs on. */
  | 'local-performance'
  /** Shared processing: office computers pooling their work over the local network. */
  | 'shared-processing'
  /** Settings > Storage: the workspace folder on this computer's disk. */
  | 'local-storage'
  /** Settings > Updates: checking for and installing a new version of the app. */
  | 'software-updates'
  /** Settings > License: trial time and activation of this computer. */
  | 'licensing'
  /** Settings > Connections and "Start from a connected app": Content Central connections saved on this computer. */
  | 'saved-connections'
  /** PAIGE: New job > From Content Central, which Paige answers with its own destination connections (S-22). */
  | 'content-central-start'
  /** CapturePoint's export: a folder on this computer or a saved connection, with filing, timing and recovery. */
  | 'desktop-export'
  /** "Move ... to another window": the detached review panels. */
  | 'detached-panels'
  /** Appearance > Text and control size: the window's own zoom. */
  | 'interface-zoom'
  /** "Folder (includes subfolders)": a folder the operator picks, read whole. A browser host has it too (its own picker). */
  | 'folder-intake'
  /** Printing break sheets: the desktop's PDF handler; Paige downloads the PDF the API draws (break-sheet-commands.ts). */
  | 'break-sheets'
  /** Import and Download job setup: a job's settings and learning as a file (Paige: a download and an upload). */
  | 'job-setup-files'
  /** Overnight learning: a run scheduled for when this computer is quiet. */
  | 'learning-schedule'
  /** Learning › Train › Start training now: a dedicated training session the engine runs on demand. */
  | 'training-session'
  /** The workspace menu's Settings entry: the desktop's own settings screen (Paige's settings are the dashboard's). */
  | 'workbench-settings'
  /** Scan jobs, the library where the operator chooses the job to work in; in Paige the job is the page's own. */
  | 'job-library'
  /** Ready-made jobs: sample paperwork installed on this computer, read through the simulated scanner. */
  | 'sample-sets'
  /** A document that was not read is read again on its own in the background (the engine's NotReadRetryCoordinator). */
  | 'not-read-retry'
  /** After setup, the rest of the folder is imported as its own phase ("setup done, now processing"). */
  | 'setup-import-phase'
  /** Ctrl+Page Down / Ctrl+Page Up reach the screen; a browser keeps them for switching its tabs. */
  | 'ctrl-page-keys'
  /** F11, Ctrl+M and Ctrl+W act on the application's own window; in a browser the window and the tab are the browser's. */
  | 'window-keys'
  /** Settings > Support: the switch that lets the application on this computer answer a local support channel. */
  | 'support-access'
  /**
   * The scanning window over a scanner this computer drives itself (TWAIN or WIA through the
   * desktop's scanner host, fed by `onScanProgress`). A host whose scanner source opens its own
   * scanning window (Paige Scan's sheet) hides it, so one scan never shows two windows.
   */
  | 'scanner-feed'
  /** Bringing a job from the earlier application installed on this computer (Import job setup › Legacy). */
  | 'legacy-import'
  /**
   * "Get more tips like this" on the setup screen: the desktop's first-launch tips email
   * (ADR-0080), sent to Ademero's list through the installation's engagement record.
   */
  | 'tips-email'
  /** CapturePoint's own orange among the accent swatches (its original brand color); Paige never uses it. */
  | 'orange-accent'
  /**
   * "Delete job…" in the job door: the workspace's own delete, which asks for the job's name. A host
   * that deletes a job somewhere else (Paige: the job's card on the jobs home, where the account's
   * rows, the stored files and the engine's copy go together) hides the tile.
   */
  | 'job-delete'
  /** Pause and Resume on a job: the worker on the operator's own computer (a Paige job has a processing mode, never a switch). */
  | 'job-pause'
  /**
   * After a confirmation the engine takes another look at the documents still waiting and lets
   * through any that now pass (the background held-work re-judge). A host that does not run it
   * does not promise it in a tip. Paige's engine host has run it since 2026-10-07
   * (`HostBackground.Rejudger`), so Paige no longer hides it.
   */
  | 'confirm-rejudge'
  /**
   * Job setup › Watched folder: a folder on the operator's own computer the desktop reads by itself
   * (its pause, set-aside list and one-document-per-file switch). Paige's watched folders are Paige
   * Scan's, set on the folder monitor, never in the workbench.
   */
  | 'watched-folder';

/** What a host that is not the desktop installs. */
export interface CapturePointHost {
  /** The features this host does not have; every entry that shows one is left out. */
  hidden: readonly HostFeature[];
  /** The words this host uses where they differ from the desktop's (`host-words.ts`). */
  words?: import('./host-words').HostWords;
  /**
   * Whether an address is a page picture this host serves. The desktop's are its own
   * `cp-preview://` scheme (page-preview-url.ts) and the snippet loader takes nothing else; a host
   * that serves its pages another way names its addresses here, and only those are let through.
   */
  pagePreviewSource?: (value: string) => boolean;
}

declare global {
  interface Window {
    capturePointHost?: CapturePointHost;
  }
}

/** Whether the host has a feature: the desktop, which installs no host, has every one. */
export function hostHas(feature: HostFeature): boolean {
  if (typeof window === 'undefined') return true;
  return !window.capturePointHost?.hidden.includes(feature);
}

/** Whether the host says an address is one of its own page pictures; the desktop, with no host, says nothing. */
export function hostServesPagePreview(value: string): boolean {
  if (typeof window === 'undefined') return false;
  return window.capturePointHost?.pagePreviewSource?.(value) ?? false;
}
